'use client';

import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { useAnalyticsContext } from '../analytics-context';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../../components/ui/chart';
import { TrendingUp, TrendingDown } from 'lucide-react';

const performanceConfig = {
  paidOrders: {
    label: 'Paid Orders',
    color: 'var(--primary)',
  },
  paidRevenue: {
    label: 'Paid Revenue',
    color: 'hsl(var(--chart-3))',
  },
};

const customerMixConfig = {
  newOrders: {
    label: 'New Customers',
    color: 'var(--primary)',
  },
  returningOrders: {
    label: 'Returning Customers',
    color: 'hsl(var(--chart-4))',
  },
};

export function ChartOrders() {
  const { orders, selectedFilter, payload, ga, baseCurrency } =
    useAnalyticsContext();

  const formatCurrency = useMemo(() => {
    const currencyCode = baseCurrency?.code || 'USD';
    const currencySymbol = baseCurrency?.symbol || '$';
    let intlFormatter: Intl.NumberFormat | null = null;
    try {
      intlFormatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      });
    } catch {
      intlFormatter = null;
    }
    return (value: number) => {
      if (!Number.isFinite(value)) {
        return '—';
      }
      if (intlFormatter) {
        return intlFormatter.format(value);
      }
      return `${currencySymbol}${value.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })}`;
    };
  }, [baseCurrency]);

  const chartData = (orders.data?.data || []).map(
    (item: {
      label: string;
      totalOrders: number;
      paidOrders: number;
      totalRevenue: number;
      paidRevenue: number;
      newOrders?: number;
      returningOrders?: number;
    }) => ({
      label: item.label,
      totalOrders: item.totalOrders,
      paidOrders: item.paidOrders,
      totalRevenue: item.totalRevenue,
      paidRevenue: item.paidRevenue,
      newOrders:
        item.newOrders !== undefined
          ? item.newOrders
          : Math.max(item.paidOrders - 1, 0),
      returningOrders:
        item.returningOrders !== undefined
          ? item.returningOrders
          : Math.max(item.paidOrders - (item.newOrders ?? 0), 0),
    }),
  );

  const isAwaitingRange = selectedFilter === 'custom' && payload === null;
  const metrics = useMemo(() => {
    const totalOrders = orders.data?.totalOrders ?? 0;
    const totalRevenue = orders.data?.totalAmount ?? 0;
    const paidOrders = orders.data?.paidOrders ?? 0;
    const paidAmount = Number(orders.data?.paidAmount ?? totalRevenue);
    if (!chartData.length) {
      return {
        totalOrders,
        totalRevenue,
        orderChange: 0,
        revenueChange: 0,
        paidOrders,
        paidAmount,
      };
    }
    const first = chartData[0];
    const last = chartData[chartData.length - 1];
    const orderChange =
      first && first.paidOrders
        ? ((last.paidOrders - first.paidOrders) / first.paidOrders) * 100
        : 0;
    const revenueChange =
      first && first.paidRevenue
        ? ((last.paidRevenue - first.paidRevenue) / first.paidRevenue) * 100
        : 0;
    return {
      totalOrders,
      totalRevenue,
      orderChange,
      revenueChange,
      paidOrders,
      paidAmount,
    };
  }, [
    chartData,
    orders.data?.totalAmount,
    orders.data?.totalOrders,
    orders.data?.paidAmount,
    orders.data?.paidOrders,
  ]);

  const sessions = ga.overview.metrics?.sessions ?? 0;
  const conversionRate =
    sessions > 0 ? ((metrics.paidOrders ?? 0) / sessions) * 100 : null;
  const averageOrderValue =
    (metrics.paidAmount ?? metrics.totalRevenue) /
    Math.max(metrics.paidOrders ?? 1, 1);
  const pendingOrders = Math.max(metrics.totalOrders - (orders.data?.completedOrders ?? 0), 0);
  const unpaidOrders = Math.max(metrics.totalOrders - (metrics.paidOrders ?? 0), 0);

  const totalNewCustomers = orders.data?.newCustomerOrders ?? 0;
  const totalReturningCustomers = orders.data?.returningCustomerOrders ?? 0;

  const orderTarget =
    chartData.length > 0
      ? ((metrics.totalOrders || 0) * 1.1) / chartData.length
      : 0;
  const revenueTarget =
    chartData.length > 0
      ? ((metrics.totalRevenue || 0) * 1.2) / chartData.length
      : 0;

  const dataWithTargets = chartData.map(point => ({
    ...point,
    orderTarget,
    revenueTarget,
  }));

  const [viewMode, setViewMode] = useState<'performance' | 'customers'>(
    'performance',
  );
  const currentConfig =
    viewMode === 'performance' ? performanceConfig : customerMixConfig;

  return (
    <Card className="overflow-hidden border-none bg-gradient-to-br from-chart-2/10 via-background to-background shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-lg font-semibold">
            Orders Overview
          </CardTitle>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[var(--primary)]" />
              <span className="text-sm text-muted-foreground">Orders:</span>
              <span className="text-sm font-medium">
                {metrics.totalOrders.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#22c55e]" />
              <span className="text-sm text-muted-foreground">Revenue:</span>
              <span className="text-sm font-medium">
                {formatCurrency(metrics.totalRevenue)}
              </span>
            </div>
          </div>
          <CardDescription className="flex items-center gap-2 mt-1">
            <span className="text-muted-foreground">Filter:</span>
            <Badge variant="outline" className="capitalize">
              {selectedFilter === 'custom'
                ? 'Custom range'
                : selectedFilter.toLowerCase()}
            </Badge>
          </CardDescription>
        </div>
        <div className="flex flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:items-end sm:gap-3">
          <span className="flex items-center gap-1">
            {metrics.orderChange >= 0 ? (
              <TrendingUp className="h-3 w-3 text-emerald-500" />
            ) : (
              <TrendingDown className="h-3 w-3 text-rose-500" />
            )}
            Orders {metrics.orderChange >= 0 ? '+' : ''}
            {metrics.orderChange.toFixed(1)}%
          </span>
          <span className="flex items-center gap-1">
            {metrics.revenueChange >= 0 ? (
              <TrendingUp className="h-3 w-3 text-emerald-500" />
            ) : (
              <TrendingDown className="h-3 w-3 text-rose-500" />
            )}
            Revenue {metrics.revenueChange >= 0 ? '+' : ''}
            {metrics.revenueChange.toFixed(1)}%
          </span>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0 space-y-4">
        <div className="grid gap-3 px-4 text-xs text-muted-foreground sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p>Avg. Order Value</p>
            <p className="text-sm font-semibold text-foreground">
              {formatCurrency(averageOrderValue)}
            </p>
          </div>
          <div>
            <p>Conversion Rate</p>
            <p className="text-sm font-semibold text-foreground">
              {conversionRate !== null
                ? `${conversionRate.toFixed(1)}%`
                : '—'}
            </p>
          </div>
          <div>
            <p>Pending / Processing</p>
            <p className="text-sm font-semibold text-foreground">
              {pendingOrders.toLocaleString()}
            </p>
          </div>
          <div>
            <p>Unpaid / Failed</p>
            <p className="text-sm font-semibold text-foreground">
              {unpaidOrders.toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 px-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="font-semibold text-foreground">
              Customer Mix:{' '}
            </span>
            <span>{totalNewCustomers.toLocaleString()} new • </span>
            <span>{totalReturningCustomers.toLocaleString()} returning</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant={viewMode === 'performance' ? 'default' : 'outline'}
              onClick={() => setViewMode('performance')}
              className="cursor-pointer"
            >
              Performance
            </Badge>
            <Badge
              variant={viewMode === 'customers' ? 'default' : 'outline'}
              onClick={() => setViewMode('customers')}
              className="cursor-pointer"
            >
              Customer Mix
            </Badge>
          </div>
        </div>
        {orders.isLoading ? (
          <div className="h-[300px] flex items-center justify-center">
            <div className="h-56 w-full animate-pulse rounded-xl bg-muted/60" />
          </div>
        ) : isAwaitingRange ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Select a start and end date to view custom data.
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data available for the selected period
          </div>
        ) : (
          <ChartContainer config={currentConfig} className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={dataWithTargets}
                margin={{ top: 16, right: 24, left: 0, bottom: 16 }}
              >
                <CartesianGrid
                  strokeDasharray="4 4"
                  className="stroke-muted/40"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  className="text-muted-foreground text-xs"
                />
                {viewMode === 'performance' ? (
                  <>
                    <YAxis
                      yAxisId="left"
                      tickMargin={8}
                      axisLine={false}
                      tickLine={false}
                      className="text-muted-foreground text-xs"
                      tickFormatter={value => value.toLocaleString()}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickMargin={8}
                      axisLine={false}
                      tickLine={false}
                      className="text-muted-foreground text-xs"
                      tickFormatter={value =>
                        formatCurrency(Number(value))
                      }
                    />
                  </>
                ) : (
                  <YAxis
                    tickMargin={8}
                    axisLine={false}
                    tickLine={false}
                    className="text-muted-foreground text-xs"
                    tickFormatter={value => value.toLocaleString()}
                  />
                )}
                <ChartTooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload, label }) => (
                    <ChartTooltipContent
                      active={active}
                      label={label}
                      payload={payload?.filter(
                        item =>
                          item.dataKey !== 'orderTarget' &&
                          item.dataKey !== 'revenueTarget',
                      )}
                    />
                  )}
                />
                {viewMode === 'performance' ? (
                  <>
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="paidOrders"
                      stroke="var(--primary)"
                      strokeWidth={2.5}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name={performanceConfig.paidOrders.label}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="orderTarget"
                      stroke="var(--primary)"
                      strokeDasharray="6 6"
                      strokeOpacity={0.4}
                      dot={false}
                      activeDot={false}
                      name="Order Goal"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="paidRevenue"
                      stroke="hsl(var(--chart-3))"
                      strokeWidth={2.5}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name={performanceConfig.paidRevenue.label}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenueTarget"
                      stroke="hsl(var(--chart-3))"
                      strokeDasharray="6 6"
                      strokeOpacity={0.4}
                      dot={false}
                      activeDot={false}
                      name="Revenue Goal"
                    />
                  </>
                ) : (
                  <>
                    <Line
                      type="monotone"
                      dataKey="newOrders"
                      stroke="var(--primary)"
                      strokeWidth={2.5}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name={customerMixConfig.newOrders.label}
                    />
                    <Line
                      type="monotone"
                      dataKey="returningOrders"
                      stroke="hsl(var(--chart-4))"
                      strokeWidth={2.5}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name={customerMixConfig.returningOrders.label}
                    />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
